import {getActiveDocument} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {ViewProps, View} from '@packages/systems/spring';
import React from 'react';

type ToolsLayerViewProps = Omit<ViewProps, 'onWheelCapture'>;

/**
 * This component is a wrapper around Spring's `View` component which can be used to render interactiver controls
 * in the Designer's Tools layer.
 *
 * By default, the tools layer has `pointer-events: none` applied to it in order to allow mouse events to pass through
 * down to the Canvas. However, in some scenarios we want to render iteractive controls that do not block wheel events. For
 * such cases, `ToolsLayerView` can be used which would allow mouse interactions, but would intercept wheel events and
 * forward them to the Canvas.
 *
 * NOTE: This component dispatches wheel events to the iframe's body, and will therefore not work over a cross-origin iframe.
 * If you need to pass wheel events to a cross-origin iframe, you will need to implement a custom `forwardWheelEventToCanvas`
 * function that uses the cross-origin communication patterns.
 */
export const ToolsLayerView = (props: ToolsLayerViewProps) => {
  const elementRef = React.useRef<HTMLDivElement | null>(null);

  const forwardWheelEventToCanvas = React.useCallback((e: WheelEvent) => {
    const activeDoc = getActiveDocument();

    if (activeDoc && activeDoc.body) {
      const syntheticEvent = new window.WheelEvent('wheel', e);
      activeDoc.body.dispatchEvent(syntheticEvent);
    }
  }, []);

  React.useEffect(() => {
    const handleWheelEvent = (event: WheelEvent) => {
      // Prevent default to avoid any of the default browser wheel event behavior
      event.stopPropagation();
      event.preventDefault();
      forwardWheelEventToCanvas(event);
    };

    const element = elementRef.current;

    if (element) {
      element.addEventListener('wheel', handleWheelEvent, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      if (element) {
        element.removeEventListener('wheel', handleWheelEvent, {capture: true});
      }
    };
  }, [forwardWheelEventToCanvas]);

  return <View {...props} pointerEvents="auto" ref={elementRef} />;
};
